import { defineAnalyticsEvent } from '@heidi/runtime/shell';

import type {
  WorkExperienceFeedbackActionedProps,
  WorkLaunchAnalyticsProps,
  WorkUpgradeActionedProps,
} from '../contracts/work-launch-analytics.types';

/**
 * The Heidi Work consolidated behavioural events (oscerai/snowplow#154), and
 * the single declaration site for each of their names (D9 ruling 27).
 *
 * FROZEN. Each `name` is the key a warehouse schema and its dashboards are
 * built on, so a rename is a new event and a second spelling silently splits a
 * metric. That is also why this is the ONLY place they are written: the app's
 * `WorkLaunchEventName` map now reads `.name` off these descriptors instead of
 * re-typing the literals, so the two cannot drift.
 *
 * `builds` is omitted throughout, i.e. `cloud-only` (D6). The on-prem
 * appliance ships without the SaaS analytics SDKs, so an event that reached
 * the on-prem registry would be a declaration nothing can emit.
 *
 * WHAT IS NOT HERE: the emit, and the routing. A package declares its events
 * and never imports a tracker (ruling 24) — `trackWorkEvent` and the
 * per-event `{ side, dashboard }` metadata stay app-side, keyed by the literal
 * names these descriptors carry.
 */
export const workSurfaceActioned = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_surface_actioned',
  description: 'A Work surface was opened, engaged with or dismissed',
});

export const workTermsActioned = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_terms_actioned',
  description: 'Work terms of use presented, accepted or declined',
});

export const workProjectCreated = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_project_created',
  description: 'A Work project was created',
});

export const workMessageSubmitted = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_message_submitted',
  description: 'A message was submitted to a Work project',
});

export const workRunStarted = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_run_started',
  description: 'An agent run started',
});

export const workRunCompleted = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_run_completed',
  description: 'An agent run reached a terminal state',
});

export const workToolCompleted = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_tool_completed',
  description: 'A tool call within a run completed',
});

export const workWorkflowActioned = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_workflow_actioned',
  description: 'A workflow/routine was created, edited or run',
});

export const workArtifactActioned = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_artifact_actioned',
  description: 'A run artifact was viewed, downloaded or pushed',
});

export const workApprovalActioned = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_approval_actioned',
  description: 'A tool approval was requested, granted or denied',
});

export const workJobCompleted = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_job_completed',
  description: 'A billable Work job completed',
});

export const workErrorEncountered = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_error_encountered',
  description: 'A Work turn or transport failed',
});

export const workMessageActioned = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()({
  name: 'work_message_actioned',
  description: 'A message was copied, retried or rated',
});

export const workVoiceHandoffCompleted = defineAnalyticsEvent<WorkLaunchAnalyticsProps>()(
  {
    name: 'work_voice_handoff_completed',
    description: 'A voice session handed off to a Work project',
  }
);

export const workExperienceFeedbackActioned =
  defineAnalyticsEvent<WorkExperienceFeedbackActionedProps>()({
    name: 'work_experience_feedback_actioned',
    description: 'UMUX-Lite experience feedback viewed, rated, snoozed or submitted',
  });

export const workUpgradeActioned = defineAnalyticsEvent<WorkUpgradeActionedProps>()({
  name: 'work_upgrade_actioned',
  description: 'Work / Agents paywall viewed, CTA clicked, or dismissed',
});
